import React, { useEffect, useState, forwardRef } from 'react';

import ActiveState from './ActiveState';
import ZeroState from './ZeroState';

type Props = {
  initialYears?: [] | [number, number];
  /**
   * A mapping of year to the # of works from the year.  Undefined is treated
   * the same as empty.
   *
   * The value will be normalized so that the maximum value always takes up an
   * appropriate amount of vertical space on the graph.
   *
   * Any year not given is assumed to be 0. ex) { 1977: 0.1, 1979: 0.7, 1980:
   * 0.45, ... }
   */
  worksPerYear?: Record<number, number>;
  /** sets the years used for the fetch request, ex. [1970, 2020] */
  setDateRangeYears: (years: Array<number>) => void;
  loading: boolean;
  /** size at which component switches to mobile version */
  mobileSize?: number;
};

const DateFilter = forwardRef(
  (
    {
      initialYears = [],
      loading,
      mobileSize = 750,
      setDateRangeYears,
      worksPerYear = {},
    }: Props,
    dateFilterRef: React.Ref<{ resetYears(): void }>,
  ) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= mobileSize);

    useEffect(() => {
      const handler = () => {
        setIsMobile(window.innerWidth <= mobileSize);
      };

      window.addEventListener('resize', handler);
      return () => {
        window.removeEventListener('resize', handler);
      };
    }, [mobileSize]);

    const yearCounts = Object.entries(worksPerYear) as unknown as [
      number,
      number,
    ][];

    if (yearCounts.length === 0) {
      return <ZeroState isMobile={isMobile} loading={loading} />;
    } else {
      const max = Math.max(...yearCounts.map((yc) => yc[1]));
      const normalizedWorksPerYear = Object.fromEntries(
        yearCounts.map(([year, count]) => [year, count / max]),
      ) as Record<number, number>;
      return (
        <ActiveState
          initialYears={initialYears}
          isMobile={isMobile}
          ref={dateFilterRef}
          setDateRangeYears={setDateRangeYears}
          worksPerYear={normalizedWorksPerYear}
        />
      );
    }
  },
);

export default DateFilter;
